import React, { useState, useEffect } from 'react';
import { getGeneratedImage } from '../../services/storage';
import { downloadImage } from '../../utils/imageUtils';

const ImageViewerScreen = ({ imageId }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        const data = await getGeneratedImage(imageId);

        if (data) {
          setImageData(data);
        } else {
          setError('Image not found or has expired');
        }
      } catch (err) {
        console.error('Error loading image:', err);
        setError('Failed to load image. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [imageId]);

  const handleDownload = () => {
    if (imageData?.imageData) {
      downloadImage(imageData.imageData, `ai-photo-booth-${imageId}.jpg`);
    }
  };

  const handleShare = async () => {
    if (navigator.share && imageData?.imageData) {
      try {
        const blob = await fetch(imageData.imageData).then(r => r.blob());
        const file = new File([blob], 'ai-photo-booth.jpg', { type: 'image/jpeg' });

        await navigator.share({
          title: 'My AI Photo Booth Memory',
          text: 'Check out my AI-transformed photo!',
          files: [file]
        });
      } catch (err) {
        console.log('Error sharing:', err);
        // Fallback to download if share fails
        handleDownload();
      }
    } else {
      handleDownload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-2xl text-white">Loading your memory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-pink-900 to-purple-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-7xl mb-6">😞</div>
          <h1 className="text-4xl font-bold text-white mb-4">Oops!</h1>
          <p className="text-xl text-red-200 mb-8">{error}</p>
          <p className="text-lg text-red-300">
            Images expire after 24 hours. If you just created this, please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full space-y-6">
        {/* Title */}
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Your AI Memory
          </h1>
          <p className="text-xl text-purple-200">
            Long press to save, or use the buttons below
          </p>
        </div>

        {/* Image - 2:3 aspect ratio */}
        <div className="relative" style={{ aspectRatio: '2/3' }}>
          <img
            src={imageData.imageData}
            alt="AI generated memory"
            className="w-full h-full rounded-2xl shadow-2xl border-2 border-purple-500/50 object-cover"
            style={{ aspectRatio: '2/3' }}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4">
          <button
            onClick={handleDownload}
            className="w-full px-8 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-2xl hover:from-purple-600 hover:to-pink-600 transition-all transform active:scale-95 shadow-xl"
          >
            💾 Download Image
          </button>

          {navigator.share && (
            <button
              onClick={handleShare}
              className="w-full px-8 py-5 bg-gradient-to-r from-[#e5181e] to-red-700 text-white rounded-2xl font-bold text-2xl hover:from-[#c01419] hover:to-red-800 transition-all transform active:scale-95 shadow-xl"
            >
              📤 Share
            </button>
          )}
        </div>

        {/* Info */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center">
          <p className="text-sm text-purple-200">
            ✨ This image will be automatically deleted after 24 hours for your privacy
          </p>
        </div>

        {/* Branding */}
        <div className="text-center pt-4">
          <p className="text-lg text-purple-300">
            Made with AI Photo Booth
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageViewerScreen;
